import { IntervalPrice, UpdatedIntervalPrice } from '../../interfaces/intervalPrice';
import { Action, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from '@angular/core';
import { AlertService } from "src/app/shared/store/alert/alert.service";
import { LocalizationService } from "src/app/services/localization/localization.service";
import { ServiceConceptActions } from "./service-concept.actions";
import { ServiceConceptsService } from "../../services/service-concepts.service";
import { MassiveActions } from "src/app/modules/massive-actions/store/massive/massive.actions";
import { ServiceConceptData, UpdatedServiceConceptData } from "../../interfaces/serviceConceptData";
import { UpdateFormDirty, UpdateFormValue } from '@ngxs/form-plugin';
import { Contract, ContractResponse } from 'src/app/modules/contract/interfaces/contract';
import { ContractData, ContractForm } from 'src/app/modules/contract/interfaces/contractData';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';
import { ConstractStudentDetails, ContractDetailEditForm, ContractDetailsAddForm, DebRepayment, DebRepaymentAddForm, DebRepaymentForm, ServiceConcept, UpdatedContractDetail, UpdatedDebRepayment } from '../../interfaces/serviceConcept';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { FormArray, FormGroup } from '@angular/forms';

export class ServiceConceptStateModel {
  contract: Contract;
  contractDataForm: {
    model: ContractForm
  }
  contractAddForm: {
    model: ContractData
  }
  serviceConceptForm: {
    model: ServiceConceptData
  };
  intervalPriceForm: {
    model: IntervalPrice
  }
  debRepaymentAddForm: {
    model: DebRepaymentAddForm
  }
  contractDetailsAddForm: {
    model: ContractDetailsAddForm
  }

  public isLoading: boolean;
  public isListSmaller: boolean;
  public isSubmitted: boolean;
  public isEdited: boolean;
  public error: string;
}

const defaults = {
  contract: new Contract(),
  contractDataForm: {
    model: null
  },
  contractAddForm: {
    model: null
  },
  serviceConceptForm: {
    model: null
  },
  intervalPriceForm: {
    model: null
  },
  debRepaymentAddForm: {
    model: null
  },
  contractDetailsAddForm: {
    model: null
  },
  isLoading: false,
  isListSmaller: true,
  isSubmitted: false,
  isEdited: false,
  error: null
}

@State<ServiceConceptStateModel>({
  name: "serviceConcepts",
  defaults
})
@Injectable()
export class ServiceConceptState {
  constructor(
    private serviceConceptsService: ServiceConceptsService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store
  ) {}

  @Action(ServiceConceptActions.GetContract)
  getContractById(ctx: StateContext<ServiceConceptStateModel>, { contractId }: ServiceConceptActions.GetContract) {
    ctx.patchState({isLoading: true});
    return this.serviceConceptsService.getContractById(contractId).subscribe({
      next: (res) =>
        ctx.dispatch(new ServiceConceptActions.GetContractSuccess(res)),
      error: (error) => {
        ctx.dispatch(new ServiceConceptActions.Error(error, ''));
      }
    });
  }

  @Action(ServiceConceptActions.GetContractSuccess)
  getContractSuccess(ctx: StateContext<ServiceConceptStateModel>, { contract }: ServiceConceptActions.GetContractSuccess) {
    const contractObj = this.getContractObj(contract);
    ctx.patchState({
      contract: contractObj,
      contractDataForm: {
        model: this.createContractFormModel(contractObj.contractData, contractObj.conceptService, contractObj.details)
      },
      isLoading: false,
    });
  }

  @Action(ServiceConceptActions.GetContractDetails)
  getContractDetails(ctx: StateContext<ServiceConceptStateModel>, { contractId }: ServiceConceptActions.GetContractDetails) {
    const state = ctx.getState();
    const contract = {...state.contract.contractData};
    this.store.dispatch(
      new UpdateFormValue({
        path: 'serviceConcepts.contractDataForm',
        value: {
            contractId: contract.contractId,
            description: contract.description,
            regularPrice: contract.regularPrice,
            unusualPercentage: contract.unusualPercentage,
            unusualPercentageDiscontinuous: contract.unusualPercentageDiscontinuous,
            percentCustody: contract.percentCustody,
            percentCustodyEnte: contract.percentCustodyEnte,
            isContractForStudents: contract.isContractForStudents,
            invoiceAnnotation: contract.invoiceAnnotation,
            offeredPriceDifference: contract.offeredPriceDifference,
            no24hRestriction: contract.no24hRestriction,
            groupFixed: contract.groupFixed,
            includedTax: contract.includedTax,
            exerciseId: contract.exerciseId,
            accountId: contract.accountId,
            descriptionConcept: contract.descriptionConcept,
            ammountUnity: contract.ammountUnity
        }
      })
    );
    this.store.dispatch(
        new UpdateFormDirty({
            dirty: false,
            path: 'serviceConcepts.contractDataForm'
        })
    );
    ctx.patchState({ isLoading: false });
  }

  @Action(ServiceConceptActions.AddContract)
  addContract(ctx: StateContext<ServiceConceptStateModel>, {contractData}: ServiceConceptActions.AddContract) {
    this.alertService.confirm(
    this.localizationService.translate("labels.contract"),
    this.localizationService.translate('masterForms.contractAddConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('masterForms.contractAddConfirm')
    .replace('{0}', contractData.description)
    ).then((result) => {
      if (result.value) {
        const contractAddModel = this.getContractAddModel(contractData);
        ctx.patchState({isLoading: true});
        return this.serviceConceptsService.createContractHeader(contractAddModel).subscribe({
          next: (res) => {
            ctx.dispatch(new ServiceConceptActions.AddContractSuccess(contractData, res.value))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, contractData.description));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.AddContractSuccess)
  addContractSuccess(ctx: StateContext<ServiceConceptStateModel>, {contractData, response}: ServiceConceptActions.AddContractSuccess) {
    const state = ctx.getState();
    const newContractData = {...contractData, contractId: response}
    const contractCopy = {...state.contract};
    const newContract = {...contractCopy, contractData: newContractData};
    ctx.patchState({
      ...state, contract: newContract,
      isLoading: false,
    });
  }

  @Action(ServiceConceptActions.EditContract)
  editContract(ctx: StateContext<ServiceConceptStateModel>, {contractDataForm, user}: ServiceConceptActions.EditContract) {
    const state = ctx.getState();
    this.alertService.confirm(
      this.localizationService.translate("labels.contract"),
      this.localizationService.translate('masterForms.contractEditConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('masterForms.contractEditConfirm')
    ).then((result) => {
      if (result.value) {
        const contractEditModel = this.getContractEditModel(contractDataForm, state);
        ctx.patchState({isLoading: true});
        return this.serviceConceptsService.updateContractHeader(contractEditModel, user).subscribe({
          next: (res) => ctx.dispatch(new ServiceConceptActions.EditContractSuccess(contractDataForm)),
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ""));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.EditContractSuccess)
  editContractSuccess(ctx: StateContext<ServiceConceptStateModel>, {}: ServiceConceptActions.EditContractSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      isLoading: false,
    });
  }

  @Action(ServiceConceptActions.AddServiceConcept)
  addServiceConcept(ctx: StateContext<ServiceConceptStateModel>, {conceptServiceData, user}: ServiceConceptActions.AddServiceConcept) {
    this.alertService.confirm(
    this.localizationService.translate("labels.service-concept"),
    this.localizationService.translate('masterForms.serviceConceptAddConceptConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('masterForms.serviceConceptAddConceptConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        const serviceConceptDataRequest = this.getServiceConceptDataReqAdd(conceptServiceData);
        return this.serviceConceptsService.createServiceConcept(serviceConceptDataRequest, user).subscribe({
          next: (res) => {
            ctx.dispatch(new ServiceConceptActions.AddServiceConceptSuccess(serviceConceptDataRequest))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, conceptServiceData.name));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.AddServiceConceptSuccess)
  addServiceConceptSuccess(ctx: StateContext<ServiceConceptStateModel>, {conceptServiceData}: ServiceConceptActions.AddServiceConceptSuccess) {
    const state = ctx.getState();
    ctx.dispatch(new ServiceConceptActions.GetContract(conceptServiceData.contractId));
    ctx.patchState({
      // ...state.contract.conceptService, ...conceptServiceData,
      isLoading: false,
    });
  }

  @Action(ServiceConceptActions.AddIntervalPrice)
  addIntervalPrice(ctx: StateContext<ServiceConceptStateModel>, {intervalPrice, user}: ServiceConceptActions.AddIntervalPrice) {
    this.alertService.confirm(
    this.localizationService.translate("labels.interval-price"),
    this.localizationService.translate('masterForms.serviceConceptAddIntervalPriceConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('masterForms.serviceConceptAddIntervalPriceConfirm')
    .replace('{0}', intervalPrice.fromAmount.toString()).replace('{1}', intervalPrice.price.toString())
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        return this.serviceConceptsService.createIntervalPrice(intervalPrice, user).subscribe({
          next: (res) => {
            ctx.dispatch(new ServiceConceptActions.AddIntervalPriceSuccess(intervalPrice, res))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, intervalPrice.fromAmount.toString()));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.AddIntervalPriceSuccess)
  addIntervalPriceSuccess(ctx: StateContext<ServiceConceptStateModel>, {intervalPrice, intervalId}: ServiceConceptActions.AddIntervalPriceSuccess) {
    let newIntervalPrice = {...intervalPrice};
    newIntervalPrice.intervalId = intervalId;
    newIntervalPrice.active = true;
    ctx.setState(
      patch({
        contract: patch({
          conceptService: updateItem<ServiceConcept>(
            (serviceConcept) => serviceConcept.conceptServiceData.conceptServiceId === intervalPrice.conceptServiceId,
            patch({
              intervalPrice: append<IntervalPrice>([newIntervalPrice])
            })
          )
        })
      })
    );
    ctx.patchState({
      isLoading: false,
    });
  }

  @Action(ServiceConceptActions.EditServiceConcept)
  editServiceConcept(ctx: StateContext<ServiceConceptStateModel>, {serviceConceptData, user}: ServiceConceptActions.EditServiceConcept) {
    const state = ctx.getState();
    this.alertService.confirm(
      this.localizationService.translate("labels.service-concept"),
      this.localizationService.translate('masterForms.serviceConceptEditConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('masterForms.serviceConceptEditConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        const serviceConceptDataRequest = this.getServiceConceptDataReqEdit(serviceConceptData);
        return this.serviceConceptsService.updateServiceConcept(serviceConceptDataRequest, user).subscribe({
          next: (res) => ctx.dispatch(new ServiceConceptActions.EditServiceConceptSuccess(serviceConceptDataRequest)),
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ""));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.EditServiceConceptSuccess)
  editServiceConceptSuccess(ctx: StateContext<ServiceConceptStateModel>, {serviceConceptData}: ServiceConceptActions.EditServiceConceptSuccess) {
    ctx.patchState({
      isLoading: false,
    });
  }

  @Action(ServiceConceptActions.EditIntervalPrices)
  editIntervalPrices(ctx: StateContext<ServiceConceptStateModel>, {conceptServiceId, connectionId, channel, user}: ServiceConceptActions.EditIntervalPrices) {
    const state = ctx.getState();
    const serviceConceptSelected = state.contract.conceptService.find(l => l.conceptServiceData.conceptServiceId == conceptServiceId);
    const intervalPricesEdited = serviceConceptSelected.intervalPrice.filter(l => l.edited === true)
    const editedRows = intervalPricesEdited.length;
    this.alertService.confirm(
      this.localizationService.translate("labels.interval-price"),
      this.localizationService.translate('masterForms.serviceConceptEditIntervalPriceConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('masterForms.serviceConceptEditIntervalPriceConfirm')
      .replace('{0}', editedRows.toString())
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        let request = {
          connectionId: connectionId,
          channel: channel,
          intervalPrices: []
        }
        request.intervalPrices = intervalPricesEdited.map(item => {
          return {
            ...item, intervalPriceId: item.intervalId, conceptServiceId: conceptServiceId,
          }
        });

        return this.serviceConceptsService.updateIntervalPrice(request, user).subscribe({
          next: (res) => ctx.dispatch(new ServiceConceptActions.EditIntervalPricesSuccess(res, conceptServiceId)),
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ""));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.EditIntervalPricesSuccess)
  editIntervalPricesSuccess(ctx: StateContext<ServiceConceptStateModel>, {massiveResponse, conceptServiceId}: ServiceConceptActions.EditIntervalPricesSuccess) {
    const state = ctx.getState();
    const serviceConceptSelected = state.contract.conceptService.find(l => l.conceptServiceData.conceptServiceId == conceptServiceId);
    const intervalPrices = serviceConceptSelected.intervalPrice.map(item => {
      return {...item, edited: false}
    });
    this.store.dispatch(new MassiveActions.SetMassiveResponse(massiveResponse))
    const serviceConceptsUpdated = state.contract.conceptService.map(row => {
      if(row.conceptServiceData.conceptServiceId === conceptServiceId) {
        return {...row, intervalPrice: intervalPrices}
      }
      return row;
    });
    ctx.patchState({
      ...state.contract.conceptService, ...serviceConceptsUpdated,
      isLoading: false,
    });
  }

  @Action(ServiceConceptActions.EditServiceConceptsTableCell)
  editServiceConceptsTableCell(ctx: StateContext<ServiceConceptStateModel>, {rowId, fieldName, newValue}: ServiceConceptActions.EditServiceConceptsTableCell) {
    const state = ctx.getState();
    const serviceConceptsUpdated = state.contract.conceptService.map(row => {
      if(row.conceptServiceData.conceptServiceId === rowId && row.conceptServiceData[fieldName] != newValue) {
        const conceptServiceData = {...row.conceptServiceData, ...{[fieldName]: newValue, edited: true}};
        const conceptService = {...row, conceptServiceData: conceptServiceData};
        return {...row, ...conceptService};
      }
      return row;
    });
    const contractCopy = {...state.contract, conceptService: serviceConceptsUpdated};
    ctx.patchState({
      contract: contractCopy,
      isLoading: false,
    });
  }

  @Action(ServiceConceptActions.EditIntervalPricesTableCell)
  editIntervalPricesTableCell(ctx: StateContext<ServiceConceptStateModel>, {conceptServiceId, rowId, fieldName, newValue}: ServiceConceptActions.EditIntervalPricesTableCell) {
    const state = ctx.getState();
    ctx.setState(
      patch({
        contract: patch({
          conceptService: updateItem<ServiceConcept>(
            (serviceConcept) => serviceConcept.conceptServiceData.conceptServiceId === conceptServiceId,
            patch({
              intervalPrice: updateItem<IntervalPrice>(
                (intervalPrice) => intervalPrice.intervalId === rowId,
                patch({
                  [fieldName]: newValue,
                  edited: true
                })
              )
            })
          )
        })
      })
    );
    ctx.patchState({
      isLoading: false,
    });

  }

  @Action(ServiceConceptActions.AddDebRepayment)
  addDebRepayment(ctx: StateContext<ServiceConceptStateModel>, {user}: ServiceConceptActions.AddDebRepayment) {
    var state = ctx.getState();
    var addDebRepayment = state.debRepaymentAddForm.model;
    this.alertService.confirm(
    this.getTitle(),
    this.localizationService.translate('debRepayment.debtRepaymentAddConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('debRepayment.debtRepaymentAddConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        const debRepaymentAddRequest = this.getDebRepaymentAddRequest(addDebRepayment);
        return this.serviceConceptsService.createDebRepayment(debRepaymentAddRequest, user).subscribe({
          next: () => {
            ctx.dispatch(new ServiceConceptActions.AddDebRepaymentSuccess(addDebRepayment))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ''));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.AddDebRepaymentSuccess)
  addDebRepaymentSuccess(ctx: StateContext<ServiceConceptStateModel>, {debRepayment}: ServiceConceptActions.AddDebRepaymentSuccess) {
    ctx.dispatch(new ServiceConceptActions.GetDebRepayments(debRepayment.serviceConceptId));
  }

  @Action(ServiceConceptActions.GetDebRepayments)
  getDebRepayments(ctx: StateContext<ServiceConceptStateModel>, {serviceConceptId}: ServiceConceptActions.GetDebRepayments) {
    ctx.patchState({isLoading: true});
    return this.serviceConceptsService.getDebRepayments(serviceConceptId).subscribe({
      next: (debRepayments) => {
        ctx.dispatch(new ServiceConceptActions.GetDebRepaymentsSuccess(debRepayments, serviceConceptId))
      },
      error: (error) => {
        ctx.dispatch(new ServiceConceptActions.Error(error, ''));
      }
    });
  }

  @Action(ServiceConceptActions.GetDebRepaymentsSuccess)
  getDebRepaymentsSGetDebRepaymentsSuccess(ctx: StateContext<ServiceConceptStateModel>, {debRepayments, serviceConceptId}: ServiceConceptActions.GetDebRepaymentsSuccess) {
    ctx.setState(
      patch({
        contract: patch({
          conceptService: updateItem<ServiceConcept>(
            (serviceConcept) => serviceConcept.conceptServiceData.conceptServiceId === serviceConceptId,
            patch({
              debRepayment: debRepayments
            }
          ))
        })
      })
    );
    ctx.patchState({ isLoading: false });
  }

  @Action(ServiceConceptActions.EditDebRepayment)
  editDebRepayment(ctx: StateContext<ServiceConceptStateModel>, {editDebRepayment, user}: ServiceConceptActions.EditDebRepayment) {
    var state = ctx.getState();
    this.alertService.confirm(
    this.getTitle(),
    this.localizationService.translate('debRepayment.debtRepaymentEditConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('debRepayment.debtRepaymentEditConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({
          isLoading: true
        });
        const editDebRepaymentRequest = this.getDebRepaymentEditRequest(editDebRepayment);
        return this.serviceConceptsService.updateDebRepayment(editDebRepaymentRequest, user).subscribe({
          next: () => {
            ctx.dispatch(new ServiceConceptActions.EditDebRepaymentSuccess(editDebRepayment))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ''));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.EditDebRepaymentSuccess)
  editDebRepaymentSuccess(ctx: StateContext<ServiceConceptStateModel>, {editDebRepayment}: ServiceConceptActions.EditDebRepaymentSuccess) {
    ctx.setState(
      patch({
        contract: patch({
          conceptService: updateItem<ServiceConcept>(
            (serviceConcept) => serviceConcept.conceptServiceData.conceptServiceId === editDebRepayment.serviceConceptId,
            patch({
              debRepayment: updateItem<DebRepayment>(
                (debRepayment) => debRepayment.debRepaymentId === editDebRepayment.debRepaymentId,
                patch({
                  debRepaymentDate: editDebRepayment.debRepaymentDate,
                  cost: editDebRepayment.cost,
                  active: editDebRepayment.active
                })
              )
            }
          ))
        })
      })
    );
    ctx.patchState({isLoading: false});
  }

  @Action(ServiceConceptActions.SetDebRepaymentAddFormDefaultState)
  setDebRepaymentAddFormDefaultState(ctx: StateContext<ServiceConceptStateModel>, {}: ServiceConceptActions.SetDebRepaymentAddFormDefaultState) {
    ctx.setState(
      patch({debRepaymentAddForm : patch({
          model: defaults.debRepaymentAddForm.model
        })
      })
    );
  }

  @Action(ServiceConceptActions.AddContractDetail)
  addContractDetail(ctx: StateContext<ServiceConceptStateModel>, {user}: ServiceConceptActions.AddContractDetail) {
    var state = ctx.getState();
    var addContractDetail = state.contractDetailsAddForm.model;
    this.alertService.confirm(
    this.getTitle(),
    this.localizationService.translate('contractStudentDetails.contractDetailsAddConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('contractStudentDetails.contractDetailsAddConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        const contractDetailAddRequest = this.getContractDetailAddRequest(addContractDetail);
        return this.serviceConceptsService.createContractDetail(contractDetailAddRequest, user).subscribe({
          next: (res) => {
            ctx.dispatch(new ServiceConceptActions.AddContractDetailSuccess(addContractDetail, res))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ''));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.AddContractDetailSuccess)
  addContractDetailSuccess(ctx: StateContext<ServiceConceptStateModel>, {contractDetail, contractDetailId}: ServiceConceptActions.AddContractDetailSuccess) {

    ctx.setState(
      patch({
        contract: patch({
          details: append<ConstractStudentDetails>([{
            contractDetailId: contractDetailId,
            contractId: contractDetail.contractId,
            sporadic: contractDetail.sporadic,
            month: contractDetail.month,
            cycleId: contractDetail.cycleId,
            isAmpa: contractDetail.isAmpa,
            priority: 0,
            scholarshipTypeId: contractDetail.scholarshipTypeId,
            advancePayment: contractDetail.advancePayment,
            advanceEnte: contractDetail.advanceEnte,
            price: contractDetail.price,
            sporadicPrice: contractDetail.sporadicPrice,
            fee: contractDetail.fee,
            advertisementDiscount: contractDetail.advertisementDiscount,
            noAdvertisementDiscount: contractDetail.noAdvertisementDiscount,
            advertisementDiscountSchoolarship: contractDetail.advertisementDiscountSchoolarship,
            noAdvertisementDiscountSchoolarship: contractDetail.noAdvertisementDiscountSchoolarship,
            justifiedDays: contractDetail.justifiedDays,
            minimumDays: contractDetail.minimumDays,
            isSchoolarshipHolderSporadic: contractDetail.isSchoolarshipHolderSporadic,
            isProportionalFee: contractDetail.isProportionalFee,
            fixedFirstDiscount: contractDetail.fixedFirstDiscount,
            fixedSecondDiscount: contractDetail.fixedSecondDiscount,
            fixedThirdDiscount: contractDetail.fixedThirdDiscount,
            fixedSchoolarshipDiscount: contractDetail.fixedSchoolarshipDiscount,
          }])
        }),
      })
    )
    ctx.patchState({isLoading: false});
  }

  @Action(ServiceConceptActions.EditContractDetail)
  editContractDetail(ctx: StateContext<ServiceConceptStateModel>, {editContractDetail, user}: ServiceConceptActions.EditContractDetail) {
    var state = ctx.getState();
    this.alertService.confirm(
    this.getTitle(),
    this.localizationService.translate('contractStudentDetails.contractDetailsEditConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('contractStudentDetails.contractDetailsEditConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({
          isLoading: true
        });
        const editContractDetailRequest = this.getContractDetailEditRequest(editContractDetail);
        return this.serviceConceptsService.updateContractDetail(editContractDetailRequest, user).subscribe({
          next: () => {
            ctx.dispatch(new ServiceConceptActions.EditContractDetailSuccess(editContractDetail))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ''));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.EditContractDetailSuccess)
  editContractDetailSuccess(ctx: StateContext<ServiceConceptStateModel>, {editContractDetail}: ServiceConceptActions.EditContractDetailSuccess) {
    ctx.setState(
      patch({
        contract: patch({
          details: updateItem<ConstractStudentDetails>(
            (detail) => detail.contractDetailId === editContractDetail.contractDetailId,
            patch({
              sporadic: editContractDetail.sporadic,
              month: editContractDetail.month,
              cycleId: editContractDetail.cycleId,
              isAmpa: editContractDetail.isAmpa,
              scholarshipTypeId: editContractDetail.scholarshipTypeId,
              advancePayment: editContractDetail.advancePayment,
              advanceEnte: editContractDetail.advanceEnte,
              price: editContractDetail.price,
              sporadicPrice: editContractDetail.sporadicPrice,
              fee: editContractDetail.fee,
              advertisementDiscount: editContractDetail.advertisementDiscount,
              noAdvertisementDiscount: editContractDetail.noAdvertisementDiscount,
              advertisementDiscountSchoolarship: editContractDetail.advertisementDiscountSchoolarship,
              noAdvertisementDiscountSchoolarship: editContractDetail.noAdvertisementDiscountSchoolarship,
              justifiedDays: editContractDetail.justifiedDays,
              minimumDays: editContractDetail.minimumDays,
              isSchoolarshipHolderSporadic: editContractDetail.isSchoolarshipHolderSporadic,
              isProportionalFee: editContractDetail.isProportionalFee,
              fixedFirstDiscount: editContractDetail.fixedFirstDiscount,
              fixedSecondDiscount: editContractDetail.fixedSecondDiscount,
              fixedThirdDiscount: editContractDetail.fixedThirdDiscount,
              fixedSchoolarshipDiscount: editContractDetail.fixedSchoolarshipDiscount,
            }
          ))
        })
      })
    );
    ctx.patchState({isLoading: false});
  }

  @Action(ServiceConceptActions.DeleteContractDetail)
  deleteContractDetail(ctx: StateContext<ServiceConceptStateModel>, {contractId, contractDetailId}: ServiceConceptActions.DeleteContractDetail) {
    var state = ctx.getState();
    this.alertService.confirm(
    this.getTitle(),
    this.localizationService.translate('contractStudentDetails.contractDetailsDeleteConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('contractStudentDetails.contractDetailsDeleteConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({
          isLoading: true
        });
        return this.serviceConceptsService.deleteContractDetail(contractId, contractDetailId).subscribe({
          next: () => {
            ctx.dispatch(new ServiceConceptActions.DeleteContractDetailSuccess(contractId, contractDetailId))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ''));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.DeleteContractDetailSuccess)
  deleteContractDetailSuccess(ctx: StateContext<ServiceConceptStateModel>, {contractId, contractDetailId}: ServiceConceptActions.DeleteContractDetailSuccess) {
    ctx.setState(
      patch({
        contract: patch({
          details: removeItem<ConstractStudentDetails>(
            (detail) => detail.contractDetailId === contractDetailId
          )
        })
      })
    );
    ctx.patchState({isLoading: false});
  }

  @Action(ServiceConceptActions.DeactivateServiceConcept)
  deactivateServiceConcept(ctx: StateContext<ServiceConceptStateModel>, {contractId, conceptServiceId, user}: ServiceConceptActions.DeactivateServiceConcept) {
    this.alertService.confirm(
    this.getTitle(),
    this.localizationService.translate('masterForms.serviceConceptDeleteConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('masterForms.serviceConceptDeleteConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({
          isLoading: true
        });
        const serviceConceptDataRequest = this.getServiceConceptDataReqDeactivate(contractId, conceptServiceId);
        return this.serviceConceptsService.deactivateServiceConcept(serviceConceptDataRequest, user).subscribe({
          next: () => {
            ctx.dispatch(new ServiceConceptActions.DeactivateServiceConceptSuccess(conceptServiceId))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptActions.Error(error, ''));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptActions.DeactivateServiceConceptSuccess)
  deactivateServiceConceptSuccess(ctx: StateContext<ServiceConceptStateModel>, {conceptServiceId}: ServiceConceptActions.DeactivateServiceConceptSuccess) {
    ctx.setState(
      patch({
        contractDataForm: patch({
          model: patch({
            serviceConcepts: removeItem<any>(
              (serviceConcept) => serviceConcept.conceptServiceId === conceptServiceId
            )
          })
        })
      })
    );
    ctx.patchState({isLoading: false});
  }

  @Action(ServiceConceptActions.SetContractDetailsAddFormDefaultState)
  setContractDetailsAddFormDefaultState(ctx: StateContext<ServiceConceptStateModel>, {}: ServiceConceptActions.SetContractDetailsAddFormDefaultState) {
    ctx.setState(
      patch({contractDetailsAddForm : patch({
          model: defaults.contractDetailsAddForm.model
        })
      })
    );
  }

  @Action(ServiceConceptActions.SetContractDataDefaultState)
  setContractDefaultState(
    ctx: StateContext<ServiceConceptStateModel>,
    {}: ServiceConceptActions.SetContractDataDefaultState
    ) {
      const state = ctx.getState();
      ctx.patchState(
        defaults
      );
    }

  @Action(ServiceConceptActions.SetServiceConceptDefaultState)
  setServiceConceptDefaultState(
    ctx: StateContext<ServiceConceptStateModel>,
    {serviceConceptDefaults}: ServiceConceptActions.SetServiceConceptDefaultState
    ) {
      const state = ctx.getState();
      ctx.patchState({
        ...state, serviceConceptForm: {model: serviceConceptDefaults}
      });
    }

  @Action(ServiceConceptActions.SetIntervalPriceDefaultState)
  setIntervalPriceDefaultState(
    ctx: StateContext<ServiceConceptStateModel>,
    {intervalPriceDefaults}: ServiceConceptActions.SetIntervalPriceDefaultState
    ) {
      const state = ctx.getState();
      ctx.patchState({
        ...state, intervalPriceForm: {model: intervalPriceDefaults}
      });
    }

  @Action(ServiceConceptActions.SetContractAddFormDefaultState)
  setSchoolsAddFormDefaultState(ctx: StateContext<ServiceConceptStateModel>, {}: ServiceConceptActions.SetContractAddFormDefaultState) {
    ctx.setState(
      patch({
        contractAddForm: patch({
          model: null
        }),
      })
    );
  }

  @Action(ServiceConceptActions.Error)
  onErrorOcurred(
    ctx: StateContext<ServiceConceptStateModel>,
    {error, details}: ServiceConceptActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoading: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  @Action(ServiceConceptActions.SetIsListSmaller)
  setIsListSmaller(
    ctx: StateContext<ServiceConceptStateModel>,
    { isListSmaller }: ServiceConceptActions.SetIsListSmaller
  ) {
    ctx.patchState({
      isListSmaller: isListSmaller,
    });
  }

  @Action(ServiceConceptActions.SetIsSubmitted)
  setIsSubmitted(
    ctx: StateContext<ServiceConceptStateModel>,
    { isSubmitted }: ServiceConceptActions.SetIsSubmitted
  ) {
    ctx.patchState({
      isSubmitted: isSubmitted,
    });
  }

  @Action(ServiceConceptActions.SetIsEdited)
  setIsEdited(
    ctx: StateContext<ServiceConceptStateModel>,
    { isEdited }: ServiceConceptActions.SetIsEdited
  ) {
    ctx.patchState({
      isEdited,
    });
  }

  private createContractFormModel(contractData: ContractData, serviceConcepts: ServiceConcept[], contractDetails: ConstractStudentDetails[]) : ContractForm {
    return {
      contractId: contractData.contractId,
      description: contractData.description,
      regularPrice: contractData.regularPrice,
      unusualPercentage: contractData.unusualPercentage,
      unusualPercentageDiscontinuous: contractData.unusualPercentageDiscontinuous,
      percentCustody: contractData.percentCustody,
      percentCustodyEnte: contractData.percentCustodyEnte,
      isContractForStudents: contractData.isContractForStudents,
      invoiceAnnotation: contractData.invoiceAnnotation,
      no24hRestriction: contractData.no24hRestriction,
      offeredPriceDifference: contractData.offeredPriceDifference,
      groupFixed: contractData.groupFixed,
      includedTax: contractData.includedTax,
      exerciseId: contractData.exerciseId,
      accountId: contractData.accountId,
      descriptionConcept: contractData.descriptionConcept,
      ammountUnity: contractData.ammountUnity,
      serviceConcepts: serviceConcepts,
      contractDetails: contractDetails
    }
  }

  private getDebRepaymentAddRequest(debRepaymentAddForm: DebRepaymentAddForm) {
    const debRepaymentAddRequest = {
      serviceConceptId: debRepaymentAddForm.serviceConceptId,
      startYear: new Date(debRepaymentAddForm.startDate).getFullYear(),
      startMonth: new Date(debRepaymentAddForm.startDate).getMonth() + 1,
      amount: debRepaymentAddForm.amount,
      feesNumber: debRepaymentAddForm.feesNumber
    }
    return debRepaymentAddRequest;
  }

  private getDebRepaymentEditRequest(debRepaymentEditForm: DebRepaymentForm) {
    const debRepaymentEditRequest = {
      debRepaymentId: debRepaymentEditForm.debRepaymentId,
      serviceConceptId: debRepaymentEditForm.serviceConceptId,
      year: new Date(debRepaymentEditForm.debRepaymentDate).getFullYear(),
      month: new Date(debRepaymentEditForm.debRepaymentDate).getMonth() + 1,
      cost: debRepaymentEditForm.cost,
      active: debRepaymentEditForm.active
    }
    return debRepaymentEditRequest;
  }

  private getContractDetailAddRequest(contractDetailsAddForm: ContractDetailsAddForm) {
    const contractDetailsAddRequest = {
      contractId: contractDetailsAddForm.contractId,
      sporadic: contractDetailsAddForm.sporadic,
      month: contractDetailsAddForm.month,
      cycleId: contractDetailsAddForm.cycleId,
      isAmpa: contractDetailsAddForm.isAmpa,
      scholarshipTypeId: contractDetailsAddForm.scholarshipTypeId,
      advancePayment: contractDetailsAddForm.advancePayment,
      advanceEnte: contractDetailsAddForm.advanceEnte,
      price: contractDetailsAddForm.price,
      sporadicPrice: contractDetailsAddForm.sporadicPrice,
      fee: contractDetailsAddForm.fee,
      advertisementDiscount: contractDetailsAddForm.advertisementDiscount,
      noAdvertisementDiscount: contractDetailsAddForm.noAdvertisementDiscount,
      advertisementDiscountSchoolarship: contractDetailsAddForm.advertisementDiscountSchoolarship,
      noAdvertisementDiscountSchoolarship: contractDetailsAddForm.noAdvertisementDiscountSchoolarship,
      justifiedDays: contractDetailsAddForm.justifiedDays,
      minimumDays: contractDetailsAddForm.minimumDays,
      isSchoolarshipHolderSporadic: contractDetailsAddForm.isSchoolarshipHolderSporadic,
      isProportionalFee: contractDetailsAddForm.isProportionalFee,
      fixedFirstDiscount: contractDetailsAddForm.fixedFirstDiscount,
      fixedSecondDiscount: contractDetailsAddForm.fixedSecondDiscount,
      fixedThirdDiscount: contractDetailsAddForm.fixedThirdDiscount,
      fixedSchoolarshipDiscount: contractDetailsAddForm.fixedSchoolarshipDiscount,
    }
    return contractDetailsAddRequest;
  }

  private getContractDetailEditRequest(contractDetailsEditForm: ContractDetailEditForm) {
    const contractDetailsEditRequest = {
      contractDetailId: contractDetailsEditForm.contractDetailId,
      contractId: contractDetailsEditForm.contractId,
      sporadic: contractDetailsEditForm.sporadic,
      month: contractDetailsEditForm.month,
      cycleId: contractDetailsEditForm.cycleId,
      isAmpa: contractDetailsEditForm.isAmpa,
      scholarshipTypeId: contractDetailsEditForm.scholarshipTypeId,
      advancePayment: contractDetailsEditForm.advancePayment,
      advanceEnte: contractDetailsEditForm.advanceEnte,
      price: contractDetailsEditForm.price,
      sporadicPrice: contractDetailsEditForm.sporadicPrice,
      fee: contractDetailsEditForm.fee,
      advertisementDiscount: contractDetailsEditForm.advertisementDiscount,
      noAdvertisementDiscount: contractDetailsEditForm.noAdvertisementDiscount,
      advertisementDiscountSchoolarship: contractDetailsEditForm.advertisementDiscountSchoolarship,
      noAdvertisementDiscountSchoolarship: contractDetailsEditForm.noAdvertisementDiscountSchoolarship,
      justifiedDays: contractDetailsEditForm.justifiedDays,
      minimumDays: contractDetailsEditForm.minimumDays,
      isSchoolarshipHolderSporadic: contractDetailsEditForm.isSchoolarshipHolderSporadic,
      isProportionalFee: contractDetailsEditForm.isProportionalFee,
      fixedFirstDiscount: contractDetailsEditForm.fixedFirstDiscount,
      fixedSecondDiscount: contractDetailsEditForm.fixedSecondDiscount,
      fixedThirdDiscount: contractDetailsEditForm.fixedThirdDiscount,
      fixedSchoolarshipDiscount: contractDetailsEditForm.fixedSchoolarshipDiscount,
    }
    return contractDetailsEditRequest;
  }

  private getContractObj(contract: ContractResponse) {
    const contractData = contract.contractData;
    const contractObj = {
      contractData: {
        contractId: contractData.contractId,
        description: contractData.description,
        regularPrice: contractData.regularPrice,
        unusualPercentage: contractData.unusualPercentage,
        unusualPercentageDiscontinuous: contractData.unusualPercentageDiscontinuous,
        percentCustody: contractData.percentCustody,
        percentCustodyEnte: contractData.percentCustodyEnte,
        isContractForStudents: contractData.isContractForStudents,
        invoiceAnnotation: contractData.invoiceAnnotation,
        no24hRestriction: contractData.no24hRestriction,
        offeredPriceDifference: contractData.offeredPriceDifference,
        groupFixed: contractData.groupFixed,
        includedTax: contractData.includedTax,
        exerciseId: contractData.exerciseId,
        accountId: contractData.groupedConcept.accountId == -1 ? "" : contractData.groupedConcept.accountId.toString(),
        descriptionConcept: contractData.groupedConcept.description,
        ammountUnity: contractData.groupedConcept.amountUnity
      },
      conceptService: contract.conceptService,
      details: contract.details
    }
    return contractObj;
  }

  private getContractAddModel(contractData: ContractData) {
    const contractDataRequest = {
      description: contractData.description,
      regularPrice: contractData.regularPrice,
      unusualPercentage: contractData.unusualPercentage,
      unusualPercentageDiscontinuous: contractData.unusualPercentageDiscontinuous,
      percentCustody: contractData.percentCustody,
      percentCustodyEnte: contractData.percentCustodyEnte,
      isContractForStudents: contractData.isContractForStudents,
      invoiceAnnotation: contractData.invoiceAnnotation,
      no24hRestriction: contractData.no24hRestriction,
      offeredPriceDifference: Number(contractData.offeredPriceDifference),
      groupFixed: contractData.groupFixed,
      includedTax: contractData.includedTax,
      exerciseId: contractData.exerciseId,
      groupedConcept: {
        accountId: contractData.accountId.toString() === "" ? -1 : Number(contractData.accountId),
        description: contractData.descriptionConcept,
        amountUnity: contractData.ammountUnity
      }
    }
    return contractDataRequest;
  }

  private getContractEditModel(contractDataForm: FormGroup, state: ServiceConceptStateModel) {
    const contractDataRequest = {
      contractId: contractDataForm.get('contractId').value,
      description: contractDataForm.get('description').value,
      regularPrice: contractDataForm.get('regularPrice').value,
      unusualPercentage: contractDataForm.get('unusualPercentage').value,
      unusualPercentageDiscontinuous: contractDataForm.get('unusualPercentageDiscontinuous').value,
      percentCustody: contractDataForm.get('percentCustody').value,
      percentCustodyEnte: contractDataForm.get('percentCustodyEnte').value,
      isContractForStudents: contractDataForm.get('isContractForStudents').value,
      invoiceAnnotation: contractDataForm.get('invoiceAnnotation').value,
      no24hRestriction: contractDataForm.get('no24hRestriction').value,
      offeredPriceDifference: Number(contractDataForm.get('offeredPriceDifference').value),
      groupFixed: contractDataForm.get('groupFixed').value,
      includedTax: contractDataForm.get('includedTax').value,
      exerciseId: state.contract.contractData.exerciseId,
      groupedConcept: {
        accountId: contractDataForm.get('accountId').value.toString() === "" ? -1 : Number(contractDataForm.get('accountId').value),
        description: contractDataForm.get('descriptionConcept').value,
        amountUnity: contractDataForm.get('ammountUnity').value
      },
      serviceConcepts: this.createUpdateServiceConcepts(contractDataForm.get('serviceConcepts') as FormArray<FormGroup>),
      contractDetails: this.createUpdateDetails(contractDataForm.get('contractDetails') as FormArray<FormGroup>)
    }
    return contractDataRequest;
  }

  private createUpdateServiceConcepts(serviceConceptsFormArray: FormArray<FormGroup>) {
    let serviceConcepts = [];
    if (!serviceConceptsFormArray) return serviceConcepts;
    serviceConceptsFormArray.controls.filter(x => x.dirty).forEach((serviceConcept: FormGroup) => {
      let serviceConceptData = new UpdatedServiceConceptData();
        serviceConceptData.conceptServiceId = serviceConcept.get('conceptServiceId').value;
        serviceConceptData.serviceConceptTypeId  = serviceConcept.get('serviceConceptTypeId').value;
        serviceConceptData.name = serviceConcept.get('name').value;
        serviceConceptData.invoiceAccountId = serviceConcept.get('invoiceAccountId').value;
        if(!serviceConcept.get('interval').value && !serviceConcept.get('redeemable').value) this.setServiceConceptPrice(serviceConcept, serviceConceptData);
        serviceConceptData.active = serviceConcept.get('active').value;
        serviceConceptData.interval = serviceConcept.get('interval').value;
        serviceConceptData.sharedInterval = serviceConcept.get('sharedInterval').value;
        serviceConceptData.billable = serviceConcept.get('billable').value;
        serviceConceptData.monitorInvoiceAccountId = serviceConcept.get('monitorInvoiceAccountId').value.toString() == "" ? 0 : Number(serviceConcept.get('monitorInvoiceAccountId').value);
        serviceConceptData.divideMonitor = serviceConcept.get('divideMonitor').value;
        serviceConceptData.percentMonitor = serviceConcept.get('percentMonitor').value.toString() == "" ? 0 : Number(serviceConcept.get('percentMonitor').value);
        serviceConceptData.scaledAccount = serviceConcept.get('scaledAccount').value;
        serviceConceptData.scalingPerDay = serviceConcept.get('scalingPerDay').value;
        serviceConceptData.groupedConcept = serviceConcept.get('groupedConcept').value == true ? 1 : 0;
        serviceConceptData.invoiceNumber = serviceConcept.get('invoiceNumber').value;
        serviceConceptData.redeemable = serviceConcept.get('redeemable').value;
        serviceConceptData.unitInInvoice = serviceConcept.get('unitInInvoice').value;
        serviceConceptData.order = Number(serviceConcept.get('order').value) || 0;
        serviceConceptData.dailyGratuities = Number(serviceConcept.get('dailyGratuities').value) || 0;
        serviceConceptData.intervalPrices = this.createUpdateIntervalPrices(serviceConcept.get('intervalPrice') as FormArray<FormGroup>);
        serviceConceptData.debRepayments = this.createUpdateDebRepayment(serviceConcept.get('debtRepayments') as FormArray<FormGroup>);
      serviceConcepts.push(serviceConceptData);
    });
    return serviceConcepts;

  }

  private setServiceConceptPrice(serviceConcept: FormGroup<any>, serviceConceptData: UpdatedServiceConceptData) {
    if (serviceConcept.get('price').value && serviceConcept.get('price').value != "") serviceConceptData.price = Number(serviceConcept.get('price').value);
  }

  createUpdateIntervalPrices(intervalPricesFormArray: FormArray<FormGroup>) {
    let intervalPrices = [];
    if (!intervalPricesFormArray) return intervalPrices;
    intervalPricesFormArray.controls.filter(x => x.dirty).forEach((intervalPrice: FormGroup) => {
      let updatedIntervalPrices = new UpdatedIntervalPrice();
        updatedIntervalPrices.intervalPriceId = intervalPrice.get('intervalPriceId').value;
        updatedIntervalPrices.fromAmount = intervalPrice.get('fromAmount').value;
        updatedIntervalPrices.price = intervalPrice.get('price').value;
        updatedIntervalPrices.active = intervalPrice.get('active').value;
        intervalPrices.push(updatedIntervalPrices);
    });
    return intervalPrices;
  }

  createUpdateDebRepayment(debRepaymentsFormArray: FormArray<FormGroup>) {
    let debRepayments = [];
    if (!debRepaymentsFormArray) return debRepayments;
    debRepaymentsFormArray.controls.filter(x => x.dirty).forEach((debRepayment: FormGroup) => {
      let updatedDebRepayment = new UpdatedDebRepayment();
      updatedDebRepayment.debRepaymentId = debRepayment.get('debRepaymentId').value,
      updatedDebRepayment.year = new Date(debRepayment.get('debRepaymentDate').value).getFullYear(),
      updatedDebRepayment.month = new Date(debRepayment.get('debRepaymentDate').value).getMonth() + 1,
      updatedDebRepayment.cost = debRepayment.get('cost').value,
      updatedDebRepayment.active = debRepayment.get('active').value,
      debRepayments.push(updatedDebRepayment);
    });
    return debRepayments;
  }

  createUpdateDetails(detailsFormArray: FormArray<FormGroup>) {
    let details = [];
    if (!detailsFormArray) return details;
    detailsFormArray.controls.filter(x => x.dirty).forEach((detail: FormGroup) => {
      let updatedDetail = new UpdatedContractDetail();
      updatedDetail.contractDetailId = detail.get('contractDetailId').value;
      updatedDetail.sporadic = detail.get('sporadic').value;
      updatedDetail.month = detail.get('month').value;
      updatedDetail.cycleId = detail.get('cycleId').value;
      updatedDetail.isAmpa = detail.get('isAmpa').value;
      updatedDetail.scholarshipTypeId = detail.get('scholarshipTypeId').value;
      updatedDetail.advancePayment = detail.get('advancePayment').value;
      updatedDetail.advanceEnte = detail.get('advanceEnte').value;
      updatedDetail.price = detail.get('price').value;
      updatedDetail.sporadicPrice = detail.get('sporadicPrice').value;
      updatedDetail.fee = detail.get('fee').value;
      updatedDetail.advertisementDiscount = detail.get('advertisementDiscount').value;
      updatedDetail.noAdvertisementDiscount = detail.get('noAdvertisementDiscount').value;
      updatedDetail.advertisementDiscountSchoolarship = detail.get('advertisementDiscountSchoolarship').value;
      updatedDetail.noAdvertisementDiscountSchoolarship = detail.get('noAdvertisementDiscountSchoolarship').value;
      updatedDetail.justifiedDays = detail.get('justifiedDays').value;
      updatedDetail.minimumDays = detail.get('minimumDays').value;
      updatedDetail.isSchoolarshipHolderSporadic = detail.get('isSchoolarshipHolderSporadic').value;
      updatedDetail.isProportionalFee = detail.get('isProportionalFee').value;
      updatedDetail.fixedFirstDiscount = detail.get('fixedFirstDiscount').value;
      updatedDetail.fixedSecondDiscount = detail.get('fixedSecondDiscount').value;
      updatedDetail.fixedThirdDiscount = detail.get('fixedThirdDiscount').value;
      updatedDetail.fixedSchoolarshipDiscount = detail.get('fixedSchoolarshipDiscount').value;
      details.push(updatedDetail);
    });
    return details;
  }

  getServiceConceptDataReqDeactivate(contractId: number, conceptServiceId: number) {
    return {
      contractId,
      conceptServiceId
    }
  }

  private getTitle() {
    return this.localizationService.translate("labels.contract");
  }

  private getServiceConceptDataReqAdd(serviceConceptData: ServiceConceptData) {
    const groupedConcept = serviceConceptData.groupedConcept ? 1 : 0;

    const serviceConceptDataRequest = {
      ...serviceConceptData,
      groupedConcept: groupedConcept,
      invoiceNumber: serviceConceptData.invoiceNumber.toString() == "" ? -1 : serviceConceptData.invoiceNumber -1,
      percentMonitor: serviceConceptData.percentMonitor.toString() == "" ? -1 : serviceConceptData.percentMonitor,
      monitorInvoiceAccountId: serviceConceptData.monitorInvoiceAccountId.toString() == "" ? -1 : serviceConceptData.monitorInvoiceAccountId,
    };

    return serviceConceptDataRequest;
  }

  private getServiceConceptDataReqEdit(serviceConceptData: ServiceConceptData): ServiceConceptData {
    const groupedConcept = serviceConceptData.groupedConcept ? 1 : 0;

    const serviceConceptDataRequest = {
      ...serviceConceptData,
      groupedConcept: groupedConcept,
      invoiceNumber: serviceConceptData.invoiceNumber.toString() == "" ? -1 : serviceConceptData.invoiceNumber,
      percentMonitor: serviceConceptData.percentMonitor.toString() == "" ? -1 : serviceConceptData.percentMonitor,
      monitorInvoiceAccountId: serviceConceptData.monitorInvoiceAccountId.toString() == "" ? -1 : serviceConceptData.monitorInvoiceAccountId,
    };
    return serviceConceptDataRequest;
  }
}
